import { c as _c } from "react/compiler-runtime";
// Блок «Метрика» на дашборде консоли (fd-analytics-05): визиты, источники,
// конверсия по целям и электронная коммерция витрины за неделю или месяц - из
// Reporting API Яндекс.Метрики через шлюз (кеш на час). Числа - как в
// интерфейсе Метрики за те же даты; ссылка ведёт туда же для сверки.
import { formatCurrency, formatNumber } from "@osmix/formatters";
import { Button } from "@osmix/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@osmix/ui/card";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { BarChart3, ExternalLink, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { api } from "@/api";
import { useMetrikaReport } from "@/api/hooks/use-analytics";
import { Segmented } from "@/components/segmented";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { useAuth } from "@/store";
const DAYS = [7, 30] as const;
type Days = (typeof DAYS)[number];
const pct = (share: number) => `${formatNumber(Math.round(share * 1000) / 10)}%`;
const day = (iso: string) => iso.split("-").reverse().join(".");
export function MetrikaSection() {
  const $ = _c(38);
  const {
    t
  } = useTranslation();
  const [days, setDays] = useState(7);
  const query = useMetrikaReport(days);
  const report = query.data;
  const user = useAuth(_temp);
  let t0;
  if ($[0] !== user) {
    t0 = hasAdminPermission(user, "action.integrations.manage");
    $[0] = user;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const canManage = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[2] !== queryClient) {
    t1 = {
      mutationFn: _temp2,
      onSuccess: () => queryClient.invalidateQueries({
        queryKey: ["analytics", "metrika"]
      })
    };
    $[2] = queryClient;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const ensureGoals = useMutation(t1);
  let t2;
  if ($[4] !== report?.sources) {
    t2 = Math.max(1, ...(report?.sources ?? []).map(_temp3));
    $[4] = report?.sources;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const maxSource = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <BarChart3 className="size-4 text-muted-foreground" />;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t) {
    t4 = t("metrikaBlock.title");
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <CardTitle className="flex items-center gap-2 text-base">{t3}{t4}</CardTitle>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t) {
    t6 = t("metrikaBlock.description");
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6) {
    t7 = <p className="text-sm text-muted-foreground">{t6}</p>;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t5 || $[16] !== t7) {
    t8 = <div>{t5}{t7}</div>;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const t9 = `${days}`;
  let t10;
  if ($[18] !== t) {
    t10 = DAYS.map(value => ({
      value: `${value}`,
      label: t(`metrikaBlock.days.${value}`)
    }));
    $[18] = t;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = value_0 => setDays(Number(value_0) as Days);
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t10 || $[22] !== t9) {
    t12 = <Segmented value={t9} options={t10} onChange={t11} />;
    $[21] = t10;
    $[22] = t9;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t12 || $[25] !== t8) {
    t13 = <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0 border-b">{t8}{t12}</CardHeader>;
    $[24] = t12;
    $[25] = t8;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== canManage || $[28] !== ensureGoals || $[29] !== maxSource || $[30] !== query.isError || $[31] !== query.isPending || $[32] !== report || $[33] !== t) {
    t14 = <CardContent className="pt-4">{query.isPending ? <p className="py-4 text-sm text-muted-foreground">{t("metrikaBlock.loading")}</p> : query.isError || !report ? <p className="py-4 text-sm text-destructive">{t("metrikaBlock.loadError")}</p> : report.status === "no_counter" ? <p className="py-4 text-sm text-muted-foreground">{t("metrikaBlock.noCounter")}{" "}<Link to="/integrations" className="underline underline-offset-2">{t("metrikaBlock.toIntegrations")}</Link></p> : report.status === "no_token" ? <p className="py-4 text-sm text-muted-foreground">{t("metrikaBlock.noToken")}</p> : report.status === "error" ? <p className="py-4 text-sm text-destructive">{t("metrikaBlock.apiError", {
          error: report.error ?? ""
        })}</p> : <div className="flex flex-col gap-5"><div className="grid grid-cols-2 gap-3 md:grid-cols-4"><Kpi label={t("metrikaBlock.visits")} value={formatNumber(report.visits)} /><Kpi label={t("metrikaBlock.users")} value={formatNumber(report.users)} /><Kpi label={t("metrikaBlock.pageviews")} value={formatNumber(report.pageviews)} /><Kpi label={t("metrikaBlock.bounceRate")} value={pct(report.bounceRate)} /></div><div className="grid gap-5 lg:grid-cols-2"><div className="min-w-0"><div className="mb-1 text-sm font-medium">{t("metrikaBlock.sources")}</div>{report.sources.length === 0 ? <p className="py-2 text-sm text-muted-foreground">{t("metrikaBlock.empty")}</p> : report.sources.map(s_1 => <div key={s_1.name} className="flex flex-col gap-1 border-b py-1.5 last:border-0"><div className="flex items-baseline justify-between gap-4"><span className="min-w-0 truncate text-sm">{s_1.name}</span><span className="flex shrink-0 items-baseline gap-2 text-right"><span className="text-sm font-semibold tabular-nums">{formatNumber(s_1.visits)}</span><span className="text-xs text-muted-foreground tabular-nums">{pct(s_1.share)}</span></span></div><div className="h-1 w-full overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-primary/30" style={{
                  width: `${Math.max(2, s_1.visits / maxSource * 100)}%`
                }} /></div></div>)}</div><div className="min-w-0"><div className="mb-1 text-sm font-medium">{t("metrikaBlock.goals")}</div>{report.goals.length === 0 ? <p className="py-2 text-sm text-muted-foreground">{t("metrikaBlock.noGoals")}</p> : <table className="w-full text-sm"><thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1 pr-2 font-normal">{t("metrikaBlock.goal")}</th><th className="py-1 pr-2 text-right font-normal">{t("metrikaBlock.reaches")}</th><th className="py-1 text-right font-normal">{t("metrikaBlock.conversion")}</th></tr></thead><tbody>{report.goals.map(_temp4)}</tbody></table>}{report.ecommerce && <div className="mt-3 grid grid-cols-2 gap-3"><Kpi label={t("metrikaBlock.purchases")} value={formatNumber(report.ecommerce.purchases)} /><Kpi label={t("metrikaBlock.revenue")} value={formatCurrency(report.ecommerce.revenue, {
                maximumFractionDigits: 0
              })} /></div>}{report.missingGoals.length > 0 && <div className="mt-3 rounded-lg bg-muted p-3 text-xs text-muted-foreground"><p>{t("metrikaBlock.missingGoals", {
                  names: report.missingGoals.map(_temp5).join(", ")
                })}</p>{canManage && <Button size="sm" variant="outline" className="mt-2" disabled={ensureGoals.isPending} onClick={() => ensureGoals.mutate()}>{ensureGoals.isPending && <Loader2 className="size-3 animate-spin" />}{t("metrikaBlock.createGoals")}</Button>}{ensureGoals.isError && <p className="mt-2 text-destructive">{ensureGoals.error instanceof Error ? ensureGoals.error.message : t("metrikaBlock.loadError")}</p>}</div>}</div></div><p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground"><span>{t("metrikaBlock.footer", {
              counter: report.counterId,
              from: day(report.periodStart),
              to: day(report.periodEnd)
            })}</span><a href={`https://metrika.yandex.ru/dashboard?id=${encodeURIComponent(report.counterId ?? "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline underline-offset-2">{t("metrikaBlock.openMetrika")}<ExternalLink className="size-3" /></a></p></div>}</CardContent>;
    $[27] = canManage;
    $[28] = ensureGoals;
    $[29] = maxSource;
    $[30] = query.isError;
    $[31] = query.isPending;
    $[32] = report;
    $[33] = t;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t13 || $[36] !== t14) {
    t15 = <Card className="overflow-hidden" data-testid="dashboard-metrika">{t13}{t14}</Card>;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  return t15;
}
function _temp5(g_0) {
  return `«${g_0.name}»`;
}
function _temp4(g) {
  return <tr key={g.id} className="border-t"><td className="py-1.5 pr-2">{g.name}</td><td className="py-1.5 pr-2 text-right tabular-nums">{formatNumber(g.reaches)}</td><td className="py-1.5 text-right tabular-nums">{pct(g.conversion)}</td></tr>;
}
function _temp3(s_0) {
  return s_0.visits;
}
function _temp2() {
  return api.analytics.ensureMetrikaGoals();
}
function _temp(s) {
  return s.user;
}
function Kpi(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <div className="text-xs text-muted-foreground">{label}</div>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <div className="mt-1 text-lg font-semibold tabular-nums">{value}</div>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="rounded-lg border p-3">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}